import { type CommonAction, response } from "@iflyrpa/share";
import { z } from "zod";
import { ActionCommonParamsSchema, AxiosProxyParams } from "../../types";
import { Http } from "../../utils/http";
import { DouyinGetLocationParamsSchema } from "./schema";

const { getABogus } = require("../../utils/douyin/douyinSafe/a_bogus.js");
const { generateCsrfTokenAdvanced } = require("../../utils/douyin/douyinSafe/csrfToken.js");

export type DouyinGetLocationParams = z.infer<typeof DouyinGetLocationParamsSchema>;
export { DouyinGetLocationParamsSchema } from "./schema";

export interface Location {
    poi_id: string;
    poi_name: string;
    poi_address: string;
    longitude: number;
    latitude: number;
    distance: number;
    city_name: string;
    district_name: string;
}

export interface DouyinLocationResponse {
    status_code: number;
    status_msg: string;
    poi_list?: Location[];
    total?: number;
    data?: {
        poi_list?: Location[];
        total?: number;
    };
}

export type DouyinGetLocationAction = CommonAction<
    DouyinGetLocationParams,
    {
        locations: Location[];
        total: number;
    }
>;

export const douyinGetLocation: DouyinGetLocationAction = async (_task, params) => {
    const userAgent =
        params.userAgent ||
        "Mozilla/5.0 (X11; Linux x86_64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/134.0.0.0 Safari/537.38";

    const headers: any = {
        cookie: params.cookies.map((it: { name: string; value: string }) => `${it.name}=${it.value}`).join(";"),
        origin: "https://creator.douyin.com",
        referer: "https://creator.douyin.com/",
        Accept: "application/json, text/plain, */*",
        "Accept-Encoding": "gzip, deflate, br, zstd",
        "Content-Type": "application/json",
        pragma: "no-cache",
        "cache-control": "no-cache",
        "sec-ch-ua-platform": params.extraParam?.browserInfo?.["sec-ch-ua-platform"] || "",
        "sec-ch-ua": params.extraParam?.browserInfo?.["sec-ch-ua"] || "",
        "sec-ch-ua-mobile": "?0",
        "user-agent": userAgent,
        "sec-fetch-site": "same-origin",
        "sec-fetch-mode": "cors",
        "sec-fetch-dest": "empty",
        "accept-language": "zh-CN,zh;q=0.9",
        priority: "u=1, i",
    };
    const args: AxiosProxyParams = [{ headers }, _task.logger, params.proxyLoc, params.accountId];
    const http = new Http(...args);

    const csrfToken = generateCsrfTokenAdvanced({
        cookies: params.cookies,
        url: "https://creator.douyin.com/aweme/v1/life/video_api/search/poi/",
        method: "GET",
        userAgent,
    });

    const keyword = params.keyword || "";
    const locationParams = {
        count: "1",
        from_webapp: "1",
        get_current_loc: "1",
        is_image_album_style: "1",
        search_type: "0",
        poi_anchor_tab: "2",
        page: "1",
        keyword: keyword,
        poi_mode: "{}",
        load_interest_city_type: "0",
        read_aid: "2906",
        aid: "1128",
        cookie_enabled: "true",
        screen_width: params.extraParam?.browserInfo?.screen_width || "1920",
        screen_height: params.extraParam?.browserInfo?.screen_height || "1080",
        browser_language: params.extraParam?.browserInfo?.browser_language || "zh-CN",
        browser_platform: params.extraParam?.browserInfo?.browser_platform || "Win32",
        browser_name: params.extraParam?.browserInfo?.browser_name || "Mozilla",
        browser_version: userAgent,
        browser_online: "true",
        timezone_name: "Asia/Shanghai",
        support_h265: "1",
        msToken: params.cookies.find((e) => e.name === "msToken")?.value || "",
        a_bogus: "",
    };
    const queryParamsBefore = new URLSearchParams(locationParams).toString();
    const aBogus = getABogus(
        `https://creator.douyin.com/aweme/v1/life/video_api/search/poi/?${queryParamsBefore}`,
        {},
        userAgent,
    );
    locationParams.a_bogus = aBogus;
    const queryString = new URLSearchParams(locationParams).toString();
    // console.log("抖音获取位置参数:", queryString);
    const res = await http.api<DouyinLocationResponse>(
        {
            method: "get",
            url: `https://creator.douyin.com/aweme/v1/life/video_api/search/poi/?${queryString}`,
            headers: {
                ...headers,
                "X-secsdk-csrf-token": csrfToken,
            },
        },
        { retries: 3, retryDelay: 20, timeout: 3000 },
    );
    console.log("抖音获取位置响应:", res);

    const isSuccess = res.status_code === 0;
    const message = `抖音获取位置${isSuccess ? "成功" : `失败，原因：${res.status_msg}`}${_task.debug ? ` ${http.proxyInfo}` : ""}`;

    let locations: Location[] = [];
    if (isSuccess && res?.poi_list) {
        if (Array.isArray(res.poi_list) && res.poi_list.length > 0) {
            if (Array.isArray(res.poi_list[0])) {
                locations = res.poi_list[0];
            } else {
                locations = res.poi_list;
            }
        }
    }

    const data: { locations: Location[]; total: number } = { locations, total: locations.length };

    return response(isSuccess ? 0 : 414, message, data);
};
